import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faMedal } from '@fortawesome/pro-regular-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CLUB_SLUG_TRANSACTIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';

import { RecentPurchase } from './RecentPurchase';
import type { RecentPurchases_tokenOperationTradeInterface } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  background: var(--c-black);
`;

type Props = {
  userSlug: string;
  transactions: RecentPurchases_tokenOperationTradeInterface[];
};
export const RecentPurchases = ({ userSlug, transactions }: Props) => {
  const { generateSportContextPath } = useSportContext();

  const cardsToDisplay = transactions.flatMap(transaction =>
    isType(transaction, 'TokenAuction') ||
    isType(transaction, 'TokenPrimaryOffer')
      ? transaction.anyCards
      : []
  );

  return (
    <Root>
      <Vertical gap={3}>
        <Horizontal>
          <FontAwesomeIcon icon={faMedal} />
          <LabelL bold>
            <FormattedMessage
              id="RecentPurchases.title"
              defaultMessage="Recent Purchases"
            />
          </LabelL>
        </Horizontal>
        {cardsToDisplay.length > 0 ? (
          <Vertical gap={1.5}>
            {cardsToDisplay.slice(0, 5).map(anyCard => (
              <RecentPurchase key={anyCard.slug} anyCard={anyCard} />
            ))}
          </Vertical>
        ) : (
          <LabelL color="var(--c-nd-600)">
            <FormattedMessage
              id="RecentPurchases.emptyState"
              defaultMessage="There is no recent purchase"
            />
          </LabelL>
        )}
        <Button
          to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_TRANSACTIONS, {
            params: { slug: userSlug },
          })}
          size="medium"
          color="quaternary"
        >
          <FormattedMessage
            id="RecentPurchases.cta.text"
            defaultMessage="View All Transactions"
          />
        </Button>
      </Vertical>
    </Root>
  );
};

RecentPurchases.fragments = {
  tokenOperationTradeInterface: gql`
    fragment RecentPurchases_tokenOperationTradeInterface on TokenOperationTradeInterface {
      id
      ... on TokenAuction {
        id
        anyCards {
          slug
          ...RecentPurchase_anyCardInterface
        }
      }
      ... on TokenPrimaryOffer {
        id
        anyCards {
          slug
          ...RecentPurchase_anyCardInterface
        }
      }
    }
    ${RecentPurchase.fragments.anyCardInterface}
  ` as TypedDocumentNode<RecentPurchases_tokenOperationTradeInterface>,
};
